import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setSelectedForm } from 'features/form/formReducer';
import { useFormClearer } from 'hooks/useFormCleaner';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  AgxButton,
  AgxModal,
  AgxRadioGroup,
  AgxSpacer,
  RadioGroupOptions,
} from '@urbanx/agx.ui-components2';
import { States } from '@urbanx/agx.agencies.client';
import { FormState } from '@urbanx/agx.campaigns.client';
import { FormConfigType } from '@urbanx/agx-ui-components';

interface NewFormButtonProps {
  id?: string;
}

export const NewFormButton = ({ id = 'agx-btn-newform6' }: NewFormButtonProps) => {
  const [showPopup, setShowPopup] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const clearFormData = useFormClearer();
  const { availableForms } = useAppSelector((state) => state.forms);
  const [selectedAustralianState, setSelectedAustralianState] = useState<States | undefined>();

  const {
    info: { agencyStates },
  } = useAppSelector((state) => state.agencies.agencyInfo);

  const selectedAgencyStatesOptions = useMemo(() => {
    return (
      agencyStates?.map(
        (state) =>
          ({
            label: state,
            value: state,
          }) as RadioGroupOptions
      ) ?? []
    );
  }, [agencyStates]);

  const onStateSelect = useCallback(() => {
    const state = selectedAustralianState as unknown as FormState;

    const form = availableForms.find(
      (af) => af.beginsCampaign && (af.state as unknown as FormState) === state
    );

    if (form) {
      clearFormData();
      dispatch(
        setSelectedForm({
          formType: form.type,
          state: form.state,
          formConfigType: form.formConfigType ?? FormConfigType.Campaign,
        })
      );
      navigate('/form');
    }

    if (!selectedAustralianState) setShowPopup(false);
  }, [availableForms, clearFormData, dispatch, navigate, selectedAustralianState]);

  const onFormButtonClicked = useCallback(() => {
    if (selectedAgencyStatesOptions.length > 1) {
      setShowPopup(true);
    } else {
      const state = selectedAgencyStatesOptions[0]?.value as unknown as States;

      setSelectedAustralianState(state);
      onStateSelect();
    }
  }, [selectedAgencyStatesOptions, onStateSelect]);

  const onStateSelectionChange = useCallback((value: string) => {
    const state = value as unknown as States;

    setSelectedAustralianState(state);
  }, []);

  return (
    <>
      <AgxButton
        text={`New Agreement`}
        onClick={onFormButtonClicked}
        variant="primary"
        size="large"
        dataTestId={id}
        id={id}
      />
      <AgxModal
        // id="statesPopup"
        type={'single'}
        variant={'withDivider'}
        actionButtonContainerStyle={'withoutDivider'}
        title="Select the state the property is located in:"
        isOpen={showPopup}
        content={
          <AgxSpacer
            direction="vertical"
            padding={{
              top: 'md',
              bottom: 'spacing-3xl',
              left: 'xl',
              right: 'xl',
            }}
          >
            <AgxRadioGroup
              id={`${id}-states-radio`}
              label={''}
              options={selectedAgencyStatesOptions}
              onChange={onStateSelectionChange}
              layout={'vertical'}
              value={selectedAustralianState}
            />
          </AgxSpacer>
        }
        setIsDialogOpen={setShowPopup}
        actionButtons={
          <AgxSpacer direction="horizontal" gap="md" contentJustification="center" fill>
            <AgxButton
              id={`${id}-states-cancel`}
              text={'Cancel'}
              variant={'hollow'}
              size="large"
              onClick={() => setShowPopup(false)}
              stretch
            />
            <AgxButton
              id={`${id}-states-select`}
              text={'Select'}
              variant={'primary'}
              size="large"
              onClick={onStateSelect}
              stretch
            />
          </AgxSpacer>
        }
      />
    </>
  );
};
